import React, { useState } from "react";
import { Loader2, Check, BellRing } from "lucide-react";
import { toast } from "sonner";
import { subscribeApi } from "../api";

/**
 * Empty-category "Notify me" opt-in form.
 * Writes to the same /api/subscribers collection with source=notify_me + category=<slug>.
 */
const NotifyMe = ({ category, categoryName }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setStatus("loading");
    try {
      await subscribeApi({ email, source: "notify_me", category });
      setStatus("done");
      setEmail("");
    } catch (err) {
      setStatus("idle");
      toast.error(err?.response?.data?.detail || "Could not save that. Try again.");
    }
  };

  return (
    <div className="max-w-md mx-auto mt-12 p-10 bg-[var(--cream)] border border-[var(--sand)]" data-testid={`notify-me-${category}`}>
      <div className="w-11 h-11 rounded-full bg-[var(--sand)] flex items-center justify-center mx-auto mb-5">
        <BellRing className="w-4 h-4 text-[var(--copper)]" />
      </div>
      <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--copper)] text-center">Studio Notes</p>
      <h3 className="font-serif-display text-2xl text-center mt-3 text-[var(--espresso)]">
        Be the first to know when <em className="italic text-[var(--copper)]">{categoryName}</em> arrive.
      </h3>
      <p className="text-sm text-center text-[var(--espresso)]/60 mt-3 leading-relaxed">
        We'll send a single quiet email the moment the first pieces are ready.
      </p>
      {status === "done" ? (
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[var(--copper)]">
          <Check className="w-4 h-4" /> You're on the list.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex mt-6">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            aria-label="Email address"
            data-testid={`notify-me-${category}-email`}
            className="flex-1 bg-transparent border border-[var(--sand)] border-r-0 h-11 px-4 text-sm text-[var(--espresso)] placeholder:text-[var(--espresso)]/35 focus:outline-none focus:border-[var(--copper)]"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            data-testid={`notify-me-${category}-submit`}
            className="h-11 px-5 bg-[var(--copper)] text-white text-[11px] tracking-[0.22em] uppercase hover:bg-[var(--copper-dark)] transition-colors flex items-center justify-center min-w-[90px]"
          >
            {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Notify"}
          </button>
        </form>
      )}
    </div>
  );
};

export default NotifyMe;
